"use client";
import { useState, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { CustomTooltip, Badge, BadgeProps } from "@/app/components/ui";
import { VersionModal } from "./version-modal";
import { api_url } from "@/app/api";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { useAuth } from "@/app/hooks";
import { Version } from "@/types";
import { CONFIG_KEYS } from "@/app/store/chat";
import { cn } from "@/app/lib/utils";

const MAX_VERSION_RETRIES = 2;

type AppVersionProps = {
   className?: string;
} & BadgeProps;
export function AppVersion({
   className,
   variant = "outline",
   ...props
}: AppVersionProps) {
   const [isOpen, setIsOpen] = useState(false);
   const [currentVersion, setCurrentVersion] = useState<Version>();
   const [versions, setVersions] = useState<Version[]>([]);
   const translation = useTranslations("AppVersion");
   const { user, isLoggedIn } = useAuth();
   const retryCountRef = useRef(0);

   useEffect(() => {
      let cancelled = false;

      const getVersion = async () => {
         try {
            const response = await fetchWithAuth(
               api_url.config_url + "?key=" + CONFIG_KEYS.appVersion
            );
            if (!response.ok) {
               return console.error("Failed to fetch app version");
            }
            const { data }: { data: Version[] } = await response.json();
            const versionsArray = Array.isArray(data) ? data : [];
            const foundVersion = versionsArray.find((v) => !v.disable);

            // Retry if backend returned empty data (cache cold-start)
            if (!foundVersion && retryCountRef.current < MAX_VERSION_RETRIES) {
               retryCountRef.current++;
               await new Promise((resolve) => setTimeout(resolve, 1000));
               if (!cancelled) {
                  return getVersion();
               }
               return;
            }

            if (!cancelled) {
               setCurrentVersion(foundVersion);
               setVersions([...versionsArray]);
            }
         } catch (error) {
            console.error("Error fetching app version:", error);
            if (!cancelled) {
               setVersions([]);
            }
         }
      };

      if (isLoggedIn) {
         getVersion();
      }

      return () => {
         cancelled = true;
      };
   }, [isLoggedIn]);

   if (!currentVersion?.version) {
      return null;
   }

   return (
      <>
         <CustomTooltip
            text={user?.isAdmin ? translation("whatNew") : "version"}
            delayDuration={1000}
         >
            <Badge
               variant={variant}
               className={cn(
                  "cursor-help border text-[10px] px-1.5 py-[0.5px] whitespace-nowrap shadow-sm transition-all duration-150",
                  "border-border/70 bg-background text-muted-foreground dark:bg-muted/50 dark:border-border/60",
                  "hover:border-primary/40 hover:bg-primary/10 hover:text-primary hover:shadow-md",
                  className
               )}
               onClick={() => user?.isAdmin && setIsOpen(true)}
               {...props}
            >
               {currentVersion.version}
            </Badge>
         </CustomTooltip>

         <VersionModal
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            versions={versions.filter((v) => !v.disable)}
         />
      </>
   );
}
